import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { SheetBlock } from '../settings/sheet';
import { Skel } from './ui';

/** Η πρώτη φόρτωση: ίδιο σχήμα με τη σελίδα (5 νούμερα, ενότητες) σε γκρι — η σελίδα δεν «πηδά» μετά. */
export const KpisSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="xh-kpis" data-count={count} aria-hidden="true">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className={`xh-kpi${i === 0 ? ' hero' : ''}`}>
        <Skel w={70} h={12} />
        <Skel w={i === 0 ? 150 : 90} h={i === 0 ? 34 : 26} className="xh-skel-gap" />
        <Skel w={80} h={10} className="xh-skel-gap" />
        <Skel w={120} h={18} className="xh-skel-gap" />
      </div>
    ))}
  </div>
);

export const SectionsSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => {
  const { t } = useTranslation(['home']);
  return (
    <div role="status" aria-label={t('home:load.loading')} data-testid="home-skeleton">
      {Array.from({ length: count }, (_, i) => (
        <SheetBlock key={i} className="xh-skel-sec">
          <div className="xh-skel-left">
            <Skel w={110} h={14} />
            <Skel w="80%" h={11} className="xh-skel-gap" />
            <Skel w={90} h={24} className="xh-skel-gap2" />
          </div>
          <Skel w="100%" h={150} className="xh-skel-chart" />
        </SheetBlock>
      ))}
      <span className="sr-only">{t('home:load.loading')}</span>
    </div>
  );
};

export const HomeLoadError: React.FC<{ onRetry: () => void; forbidden?: boolean; message?: string | null }> = ({ onRetry, forbidden = false, message }) => {
  const { t } = useTranslation(['home']);
  return (
    <div role="alert" className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-2xl border border-red-500/30 bg-red-500/10" data-testid="home-error">
      <AlertTriangle className="w-6 h-6 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-t1">{forbidden ? t('home:load.forbiddenTitle') : t('home:load.errorTitle')}</p>
        <p className="text-xs text-t3 mt-0.5">{forbidden ? t('home:load.forbiddenHint') : (message || t('home:load.errorHint'))}</p>
      </div>
      {!forbidden && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 transition"
          data-testid="home-retry"
        >
          <RefreshCw className="w-4 h-4" aria-hidden="true" />
          {t('home:load.retry')}
        </button>
      )}
    </div>
  );
};
